import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '../../lib/api'
import { FACEBOOK_POSTING } from '../../lib/features'
import { formatDate, plural } from '../../lib/format'
import { prepareInstagramImages } from '../../lib/instagram'
import { ArtistComic, FacebookConnection, InstagramConnection, SocialPost } from '../../lib/types'
import { useLoad } from '../../lib/useLoad'
import { Dialog } from '../Dialog'
import { Icon } from '../Icon'
import { Alert, Loading, Sk } from '../states'

export type Platform = 'instagram' | 'facebook'

interface PlatformPreview<C> {
  connection: C
  caption: string
  panels: number
  post: SocialPost | null
}

interface Preview {
  instagram: PlatformPreview<InstagramConnection>
  facebook: PlatformPreview<FacebookConnection>
}

type Step = { kind: 'idle' } | { kind: 'starting'; done: number; total: number } | { kind: 'failed'; message: string }

const POLL_MS = 4000
const GIVE_UP_MS = 10 * 60 * 1000
const NAMES: Record<Platform, string> = { instagram: 'Instagram', facebook: 'Facebook' }
const PLATFORMS: Platform[] = FACEBOOK_POSTING ? ['instagram', 'facebook'] : ['instagram']

/** Name of the posting window, and of the buttons that open it */
export const SOCIAL_TITLE = FACEBOOK_POSTING ? 'Post to Instagram & Facebook' : 'Post to Instagram'

/**
 * Post a published comic to Instagram and the Facebook Page: shows each caption, prepares
 * the Instagram panels, starts the posts, then follows them until they're done.
 */
export function SocialDialog({
  comic,
  onClose,
  onChange,
}: {
  comic: ArtistComic
  onClose: () => void
  onChange?: (platform: Platform, post: SocialPost) => void
}) {
  const load = useLoad((signal) => api.get<Preview>(`/api/admin/comics/${comic.id}/social`, signal), [comic.id])
  const [posts, setPosts] = useState<Partial<Record<Platform, SocialPost>>>({})
  const [picked, setPicked] = useState<Record<Platform, boolean> | null>(null)
  const [step, setStep] = useState<Step>({ kind: 'idle' })
  const [startedHere, setStartedHere] = useState<Platform[]>([])
  const [slow, setSlow] = useState(false)
  const abort = useRef<AbortController | null>(null)

  const data = load.state === 'ready' ? load.data : null
  const current = (p: Platform): SocialPost | null => posts[p] ?? data?.[p].post ?? null
  const connected = (p: Platform) => Boolean(data?.[p].connection.connected)

  // Tick each connected platform that hasn't had this comic yet
  useEffect(() => {
    if (!data || picked) return
    const fresh = (p: Platform) => connected(p) && !['posted', 'pending'].includes(data[p].post?.status ?? '')
    setPicked({ instagram: fresh('instagram'), facebook: FACEBOOK_POSTING && fresh('facebook') })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, picked])

  const update = (p: Platform, post: SocialPost) => {
    setPosts((all) => ({ ...all, [p]: post }))
    onChange?.(p, post)
  }

  // Follow posts that are under way until Meta says they're done
  const pendingKey = PLATFORMS.filter((p) => current(p)?.status === 'pending').join(',')
  useEffect(() => {
    if (!pendingKey) return
    const started = Date.now()
    let stopped = false
    let timer: ReturnType<typeof setTimeout>
    const tick = async () => {
      if (stopped) return
      try {
        const res = await api.get<Preview>(`/api/admin/comics/${comic.id}/social`)
        if (stopped) return
        for (const p of PLATFORMS) {
          const post = res[p].post
          if (post && post.status !== 'pending' && pendingKey.includes(p)) update(p, post)
        }
      } catch {
        // keep waiting; a dropped connection shouldn't stop the follow-up
      }
      if (Date.now() - started > GIVE_UP_MS) {
        setSlow(true)
        return
      }
      timer = setTimeout(tick, POLL_MS)
    }
    timer = setTimeout(tick, POLL_MS)
    return () => {
      stopped = true
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey, comic.id])

  useEffect(() => () => abort.current?.abort(), [])

  const chosen = picked ? PLATFORMS.filter((p) => picked[p] && connected(p)) : []

  const start = async () => {
    abort.current = new AbortController()
    const total = Math.min(comic.images.length, 10)
    setStep({ kind: 'starting', done: 0, total })
    setSlow(false)
    try {
      if (chosen.includes('facebook')) {
        const res = await api.post<{ post: SocialPost }>(`/api/admin/comics/${comic.id}/facebook`)
        update('facebook', res.post)
        setStartedHere((s) => [...s, 'facebook'])
        setPicked((x) => x && { ...x, facebook: false })
      }
      if (chosen.includes('instagram')) {
        const images = await prepareInstagramImages(comic.images, (done) => setStep({ kind: 'starting', done, total }), abort.current.signal)
        const res = await api.post<{ post: SocialPost }>(`/api/admin/comics/${comic.id}/instagram`, { images })
        update('instagram', res.post)
        setStartedHere((s) => [...s, 'instagram'])
        setPicked((x) => x && { ...x, instagram: false })
      }
      setStep({ kind: 'idle' })
    } catch (error) {
      if ((error as Error).name === 'AbortError') return
      setStep({
        kind: 'failed',
        message: error instanceof ApiError && error.status ? error.message : "The post couldn't be started. Check your connection and try again.",
      })
    }
  }

  const close = () => {
    if (step.kind === 'starting') abort.current?.abort()
    onClose()
  }

  const section = (p: Platform) => {
    if (!data || !picked) return null
    const preview = data[p]
    const post = current(p)
    const who =
      p === 'instagram' ? (data.instagram.connection.username ? `@${data.instagram.connection.username}` : '') : (data.facebook.connection.pageName ?? '')
    const id = `soc-${p}`
    return (
      <section key={p} className="vstack gap-sm social-section" aria-labelledby={`${id}-name`}>
        <label className="hstack gap-sm check">
          <input
            type="checkbox"
            checked={picked[p] && connected(p)}
            disabled={!connected(p) || step.kind === 'starting' || post?.status === 'pending'}
            onChange={(e) => setPicked({ ...picked, [p]: e.target.checked })}
          />
          <strong id={`${id}-name`}>{p === 'instagram' ? 'Instagram' : 'Facebook Page'}</strong>
          {who && <span className="hint">{who}</span>}
        </label>
        {!connected(p) ? (
          <div className="hint">
            Not connected. Connect it once in <Link to="/artist/settings">Settings</Link>.
          </div>
        ) : (
          <>
            {post?.status === 'pending' && (
              <div className="hstack gap-sm hint strong" role="status">
                <span className="spin" aria-hidden="true" />
                Posting to {NAMES[p]}…
              </div>
            )}
            {post?.status === 'posted' && (
              <div className="hstack gap-sm hint strong" role="status">
                <Icon name="okCircle" size={18} />
                {startedHere.includes(p) ? 'Posted.' : `Posted on ${formatDate(post.updatedAt)}.`}
                {post.postUrl && (
                  <a href={post.postUrl} target="_blank" rel="noreferrer">
                    View post
                  </a>
                )}
              </div>
            )}
            {post?.status === 'failed' && (
              <div className="ferr" role="alert">
                <Icon name="alert" size={16} />
                {post.error}
              </div>
            )}
            <div className="caption-preview">{preview.caption}</div>
            <div className="hint">
              {plural(preview.panels, 'panel')}
              {p === 'instagram' && preview.panels > 1 ? ', padded with white to one shape so nothing is cropped.' : '.'}
              {post?.status === 'posted' && ' Posting again makes a second post.'}
            </div>
          </>
        )}
      </section>
    )
  }

  let body
  if (load.state === 'loading') {
    body = (
      <Loading label="Getting the posts ready">
        <Sk h={160} />
      </Loading>
    )
  } else if (load.state === 'error') {
    body = (
      <Alert title="This couldn't be loaded" actions={<button type="button" className="btn sm" onClick={load.retry}>Try again</button>}>
        Check your connection and try again.
      </Alert>
    )
  } else {
    body = (
      <>
        {step.kind === 'failed' && <Alert title="That didn't work.">{step.message}</Alert>}
        {step.kind === 'starting' && chosen.includes('instagram') && (
          <Alert tone="info" title={`Preparing panels for Instagram (${step.done} of ${step.total})`}>
            Please keep this window open.
          </Alert>
        )}
        {pendingKey && (
          <Alert tone="info" title={slow ? "It's taking longer than usual." : 'Posting…'}>
            {slow
              ? 'Close this and check Manage comics in a few minutes.'
              : 'This usually takes under a minute. You can close this window: posting carries on, and Manage comics shows how it went.'}
          </Alert>
        )}
        {PLATFORMS.map(section)}
        <div className="hint">To change a caption or the tags, edit the comic first.</div>
      </>
    )
  }

  const busy = step.kind === 'starting'

  return (
    <Dialog labelledBy="soc-title" onClose={close} className="dialog social" center>
      <div className="dialog-head">
        <div>
          <h2 id="soc-title">{SOCIAL_TITLE}</h2>
          <div className="hint">{comic.title}</div>
        </div>
        <button type="button" className="iconbtn" aria-label="Close" onClick={close}>
          <Icon name="close" />
        </button>
      </div>
      <div className="dialog-body vstack gap-lg">{body}</div>
      <div className="dialog-foot">
        <button type="button" className="btn" onClick={close}>
          {busy || pendingKey ? 'Close' : 'Done'}
        </button>
        {data && (
          <button type="button" className="btn solid" disabled={busy || chosen.length === 0} onClick={start}>
            {busy && <span className="spin" aria-hidden="true" />}
            {busy ? 'Starting' : chosen.length === 2 ? 'Post to both' : chosen.length === 1 ? `Post to ${NAMES[chosen[0]]}` : 'Post'}
          </button>
        )}
      </div>
    </Dialog>
  )
}

/** Small labels for Manage comics */
export function SocialBadges({ comic }: { comic: ArtistComic }) {
  return (
    <>
      {PLATFORMS.map((p) => {
        const post = comic[p]
        if (!post) return null
        if (post.status === 'posted') {
          return post.postUrl ? (
            <a key={p} className="pill posted" href={post.postUrl} target="_blank" rel="noreferrer">
              On {NAMES[p]}
            </a>
          ) : (
            <span key={p} className="pill posted">
              On {NAMES[p]}
            </span>
          )
        }
        if (post.status === 'pending') {
          return (
            <span key={p} className="pill draft">
              Posting to {NAMES[p]}…
            </span>
          )
        }
        return (
          <span key={p} className="pill failed" title={post.error}>
            {NAMES[p]} post failed
          </span>
        )
      })}
    </>
  )
}
